import type { World } from './types';
import { civilFromEpochDay, daysInMonth, epochDayFromCivil, epochDayOf } from './calendar';
import { ECONOMY } from './economy';
import { SURVEY_NAMES, planDays, planWait, ruleWords } from './operations';
import { PROGRAMS, nextScheduledCount } from './monitoring';
import { SPECIES } from './species';
import { ISLAND_NAMES, REEF_ISLANDS, type ReefIsland } from './reef';

/**
 * Advance to the next event (M240, the overhaul's brief, section 9): the next day something the player waits on
 * happens, read from the world (a survey home, a consultation over, a rule made, a monitoring program's count, the
 * month's accounts, the grant, the warm season's turn, the annual review). The HUD steps the world day by day to it,
 * every system on every day as the tick steps it, never a precomputed outcome, and stops early on any warning the days
 * bring (`stopsAdvance`).
 */
export interface NextEvent {
  /** The epoch day it falls on. */
  day: number;
  /** What it is, as the button tells it. */
  what: string;
}

export function nextEvent(world: World): NextEvent | null {
  const today = epochDayOf(world);
  const found: NextEvent[] = [];
  for (const j of world.operations.jobs) {
    const end = j.phaseEnds.find((d) => d > today);
    if (end === undefined) continue;
    if (j.kind === 'survey' && j.survey) found.push({ day: end, what: `${SURVEY_NAMES[j.survey]} home` });
    else if (j.rule) found.push({ day: end, what: end === j.phaseEnds[0] ? 'Consultation over' : `${ruleWords(j.rule)} in force` });
    // Maui Nui's whole setup too, its job no island's (M327; review 34: it was never an event, the advance running past it).
    else if (j.kind === 'community') found.push({ day: end, what: `${j.island ? ISLAND_NAMES[j.island] : 'Maui Nui'}'s volunteers ready` });
  }
  // A trip waiting in the plan home on the day the plan dates it (M309's review: a cleanup behind a survey came home
  // unseen, the advance running ten days past it to the month's accounts).
  // None while the plan's first trip waits on what only the player can give, its cost or its crew (review 34: a trip
  // the conservancy could not pay for stopped the advance at a homecoming that never came, again and again).
  // (With every boat out the plan's homes fall after the first boat's own, and once one is home `planWait` holds the trip.)
  const plan = world.operations.plan;
  if (planWait(world) === null) for (const d of planDays(world)) {
    const p = plan.find((x) => x.id === d.id);
    if (p && d.home !== null && d.home > today) found.push({ day: d.home, what: `${SURVEY_NAMES[p.kind]} home` });
  }
  // The second boat in the water and an island's volunteers ready (M277).
  for (const p of world.operations.projects) if (p.readyDay > today) found.push({ day: p.readyDay, what: `The ${ECONOMY.secondBoat.name} in the water` });
  // A patrol's term over (M274): the day to buy another, or not.
  const m = world.sanctuaries[0]?.management;
  m?.patrolUntil.forEach((until, i) => { if (until !== null && until > today) found.push({ day: until, what: `${ISLAND_NAMES[REEF_ISLANDS[i] as ReefIsland]}'s patrol ends` }); });
  const c = civilFromEpochDay(today);
  // The campaign's days only where it runs, a sanctuary with a reef (M240's review: at the Cape they never came).
  const reef = !!world.sanctuaries[0]?.coral && !!world.sanctuaries[0]?.fish;
  if (reef && world.campaign.reviewDay > today) found.push({ day: world.campaign.reviewDay, what: 'The annual review' });
  if (reef && world.campaign.test === null) {
    const season = epochDayFromCivil(c.year, 7, 1);
    if (season > today) found.push({ day: season, what: 'The warm season' });
  }
  // Only the counts of what lives at the sanctuary (M240's review: the right whale's and the gray whale's, counted
  // world-wide, had stopped an advance at Maui Nui on a day nothing there changed): a migrant where a source places it
  // at the sanctuary's site, anything else where its program measures it there.
  const site = world.sanctuaries[0]?.siteId;
  const here = (id: string): boolean => {
    const r = SPECIES.find((x) => x.id === id);
    return r?.model.kind === 'migrant' ? site !== undefined && (r.atSites ?? []).includes(site) : (PROGRAMS[id]?.measure?.(world) ?? null) !== null;
  };
  const count = nextScheduledCount(today, here);
  if (count) found.push({ day: count.day, what: count.ids.length > 1 ? 'Monitoring counts' : 'A monitoring count' });
  const monthEnd = epochDayFromCivil(c.year, c.month, daysInMonth(c.year, c.month));
  found.push({ day: monthEnd > today ? monthEnd : epochDayFromCivil(c.month === 12 ? c.year + 1 : c.year, c.month === 12 ? 1 : c.month + 1, daysInMonth(c.month === 12 ? c.year + 1 : c.year, c.month === 12 ? 1 : c.month + 1)), what: 'The month\'s accounts' });
  const grant = epochDayFromCivil(c.year, ECONOMY.grantDay.month, ECONOMY.grantDay.day);
  found.push({ day: grant > today ? grant : epochDayFromCivil(c.year + 1, ECONOMY.grantDay.month, ECONOMY.grantDay.day), what: 'The operating grant' });
  // The earliest; on a tie, the first found (the player's own work, then the campaign's, then the counts and the
  // calendar's: the review, not the monk seals' count or December's accounts, on 31 December).
  return found.reduce<NextEvent | null>((best, e) => (!best || e.day < best.day ? e : best), null);
}

/** Whether a day's events stop an advance: anything a player should see as it happens (a warning or worse). */
export const stopsAdvance = (severity: string): boolean => severity === 'warning' || severity === 'critical';
